import { useLayoutEffect, useRef } from 'react'

import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

/**
 * Manages height calculation for elements that switch to fixed/sticky positioning, preventing CLS by maintaining wrapper height.
 * @param enabled - Whether to enable the behavior (e.g., isMobile && experimentActive)
 * @param dependencies - Array of dependencies that should trigger height recalculation
 */
interface UseStickyWrapperHeightOptions {
  enabled: boolean
  dependencies?: unknown[]
}

export const useStickyWrapperHeight = (options: UseStickyWrapperHeightOptions) => {
  const { enabled, dependencies = [] } = options

  const wrapperRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!isBrowser() || !enabled) {
      return
    }

    const updateWrapperHeight = () => {
      if (containerRef.current && wrapperRef.current) {
        const isCurrentlyFixed =
          containerRef.current.style.position === 'fixed' ||
          window.getComputedStyle(containerRef.current).position === 'fixed'

        if (!isCurrentlyFixed) {
          const containerHeight = containerRef.current.getBoundingClientRect().height
          wrapperRef.current.style.height = `${containerHeight}px`
        }
      }
    }

    updateWrapperHeight()
    window.addEventListener('resize', updateWrapperHeight)

    let resizeObserver: ResizeObserver | null = null
    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateWrapperHeight()
      })
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      window.removeEventListener('resize', updateWrapperHeight)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...dependencies])

  return { wrapperRef, containerRef }
}
